import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from "react-router-dom";
import { useEffect, useState, Component, type ReactNode } from "react";
import Navbar from "./components/Navbar";

import Footer from "./components/Footer";
import CartDrawer from "./components/CartDrawer";
import HomePage from "./pages/HomePage";
import ShopPage from "./pages/ShopPage";
import ProductPage from "./pages/ProductPage";
import CheckoutPage from "./pages/CheckoutPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import AccountPage from "./pages/AccountPage";
import AboutPage from "./pages/AboutPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import TokushohoPage from "./pages/TokushohoPage";
import FaqPage from "./pages/FaqPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import OnboardingPage from "./pages/OnboardingPage";
import AiCardCreator from "./components/AiCardCreator";
import CookieConsent from "./components/CookieConsent";
import UmamiAnalytics from "./components/UmamiAnalytics";
import { useAuthStore } from "./store/auth";
import { useCartStore } from "./store/cart";
import { useCurrencyStore } from "./store/currency";
import { useTenant } from "./lib/tenant";
import api from "./lib/api";
import { useTranslation } from "react-i18next";

class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: "2rem", fontFamily: "sans-serif", maxWidth: 600, margin: "4rem auto" }}>
          <h2 style={{ color: "#b91c1c" }}>Something went wrong</h2>
          <pre style={{ background: "#f3f4f6", padding: "1rem", borderRadius: 6, overflowX: "auto", fontSize: 13 }}>
            {(this.state.error as Error).message}
            {"\n\n"}
            {(this.state.error as Error).stack}
          </pre>
          <button onClick={() => window.location.reload()} style={{ marginTop: "1rem", padding: "0.5rem 1rem", cursor: "pointer" }}>
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user } = useAuthStore();
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </ErrorBoundary>
  );
}

function AppContent() {
  const { user } = useAuthStore();
  const { fetch: fetchCart } = useCartStore();
  const { setEnabledCurrencies, setAiEnabled, setUmamiWebsiteId, siteMode, maintenanceMessage, setSiteMode, setMaintenanceMessage } = useCurrencyStore();
  const tenant = useTenant();
  const { i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);

  useEffect(() => {
    if (user) fetchCart();
  }, [user, fetchCart]);

  useEffect(() => {
    // Secret admin bypass: if ?admin_access=true is present, set a flag in session storage
    if (searchParams.get("admin_access") === "true") {
      sessionStorage.setItem("admin_bypass", "true");
    }
  }, [searchParams]);

  useEffect(() => {
    // Fetch enabled currencies and features
    api.get("/settings/public").then(res => {
      if (res.data.enabledCurrencies) {
        setEnabledCurrencies(res.data.enabledCurrencies);
      }
      if (res.data.aiEnabled !== undefined) {
        setAiEnabled(res.data.aiEnabled);
      }
      if (res.data.umamiWebsiteId !== undefined) {
        setUmamiWebsiteId(res.data.umamiWebsiteId);
      }
      if (res.data.siteMode) {
        setSiteMode(res.data.siteMode);
      }
      if (res.data.maintenanceMessage) {
        setMaintenanceMessage(res.data.maintenanceMessage);
      }
    }).catch(err => {
      console.error("Failed to fetch settings", err);
    }).finally(() => {
      setIsLoadingSettings(false);
    });
  }, [setEnabledCurrencies, setAiEnabled, setUmamiWebsiteId, setSiteMode, setMaintenanceMessage, tenant.id]);

  useEffect(() => {
    // Set CSS variables for branding
    const root = document.documentElement;
    // themeColor is "rgb(r, g, b)"
    const color = tenant.themeColor.replace("rgb(", "").replace(")", "").split(",").map(c => c.trim()).join(" ");
    root.style.setProperty("--brand-color", color);
    root.style.setProperty("--display-font", tenant.fonts.display);
    root.style.setProperty("--sans-font", tenant.fonts.sans);
    root.style.setProperty("--logo-height", tenant.logoHeight);
    
    // Update document title
    document.title = tenant.name;
    
    // Add serif font if needed (Chlorine)
    if (tenant.id === "chlorine_fine_art") {
      const link = document.createElement("link");
      link.id = "premium-font";
      link.href = "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400..900;1,400..900&display=swap";
      link.rel = "stylesheet";
      if (!document.getElementById("premium-font")) {
        document.head.appendChild(link);
      }
    }
  }, [tenant]);

  const isAdminBypassed = sessionStorage.getItem("admin_bypass") === "true" || user?.role === "admin";

  // Prevent flash: show a professional loading state until site mode is confirmed
  if (isLoadingSettings) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-pulse">
          <img
            src={`/branding/${tenant.id}/logo.${tenant.id === 'chlorine.art' ? 'svg' : 'png'}`}
            alt={tenant.name}
            style={{ height: "40px", opacity: 0.5 }}
          />
        </div>
      </div>
    );
  }

  if (siteMode === "maintenance" && !isAdminBypassed) {
    const displayMessage = typeof maintenanceMessage === 'object' 
      ? (maintenanceMessage[i18n.language] || maintenanceMessage['en'] || Object.values(maintenanceMessage)[0])
      : maintenanceMessage;

    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-4">
        <div className="max-w-md w-full text-center space-y-12">
          <img
            src={`/branding/${tenant.id}/logo.${tenant.id === 'chlorine.art' ? 'svg' : 'png'}`}
            alt={tenant.name}
            className="mx-auto"
            style={{ height: "60px" }}
          />
          <div className="space-y-6">
            <h1 className="text-sm font-bold tracking-[0.2em] uppercase text-gray-400">Maintenance</h1>
            <p className="text-xl font-display text-gray-900 leading-relaxed">
              {displayMessage || "We are currently performing maintenance. Please check back later."}
            </p>
          </div>
          <div className="pt-12">
            <div className="h-0.5 w-12 bg-gray-200 mx-auto" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col min-h-screen tenant-${tenant.id}`}>
      {siteMode === "maintenance" && user?.role === "admin" && (
        <div className="bg-red-600 text-white text-[10px] font-bold py-1.5 px-4 text-center tracking-widest uppercase sticky top-0 z-[60]">
          Admin View: Site is in Maintenance Mode
        </div>
      )}
      <Navbar />
      <CartDrawer />
      <div className="flex-1">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/shop" element={<ShopPage />} />
          <Route path="/shop/:slug" element={<ProductPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route
            path="/account"
            element={
              <RequireAuth>
                <AccountPage />
              </RequireAuth>
            }
          />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/tokushoho" element={<TokushohoPage />} />
          <Route path="/faq" element={<FaqPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route
            path="/onboarding"
            element={
              <RequireAuth>
                <OnboardingPage />
              </RequireAuth>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
      <Footer />
      <AiCardCreator />
      <CookieConsent />
      <UmamiAnalytics />
    </div>
  );
}
